import { useMemo, useRef, useEffect } from 'react';
import gsap from 'gsap';
import { useRouter } from 'next/router';

//* HOC's
import withUIContext from 'context/consumerHOC/UIConsumer';

//* Components
import Text from 'components/common/Text';
import ProductCard from 'components/common/ProductCard';
import Button from 'components/common/Button';

//* Helpers
import { useActiveSection, letterSpacingAnimation } from 'helpers';

//* Styles
import CollectionStyle from './style';

const Collection = ({ id, name, slug, text, products, key, winWidth, isOpened, onCollapseClick, isGatesActive }) => {
	//! Refs
	const sectionRef = useRef();
	const bodyRef = useRef();
	const titleRef = useRef();
	const textRef = useRef();
	const productsRef = useRef([]);
	const isAnimated = useRef(false);

	//! Router
	const router = useRouter();

	//! States
	const activeSection = useActiveSection(sectionRef, 70);

	//! Checking is collapse
	const isCollapse = useMemo(() => winWidth < 768, [winWidth]);

	useEffect(() => {
		const isActive = (isCollapse && isOpened) || !isCollapse;

		if (isCollapse && isOpened) {
			const sectionObj = sectionRef.current.getBoundingClientRect();

			window.scrollTo({ top: sectionObj.top });
		}

		gsap.killTweensOf(bodyRef.current);
		gsap.to(bodyRef.current, { duration: isActive ? products.length * 0.3 : 0.01, height: isActive ? 'auto' : 0 });
	}, [isOpened, isCollapse, products]);

	useEffect(() => {
		if (!isAnimated.current) {
			if (activeSection && !isGatesActive) {
				isAnimated.current = true;
				gsap.killTweensOf([...productsRef.current, titleRef.current, textRef.current]);
				gsap.set(productsRef.current, { opacity: 1 });

				if (!isCollapse) {
					gsap.set(productsRef.current, { opacity: 1 });
					gsap.fromTo(
						productsRef.current,
						{ y: '100%' },
						{ y: '0%', delay: 0.2, duration: 0.8, stagger: 0.1 }
					);
					letterSpacingAnimation([titleRef.current]);
					gsap.to(textRef.current, { delay: 0.5, duration: 1, opacity: 1 });
				} else {
					gsap.set(productsRef.current, { y: '0%', opacity: 1 });
					gsap.set([titleRef.current, textRef.current], { opacity: 1 });
				}
			} else {
				gsap.set([...productsRef.current, titleRef.current, textRef.current], { opacity: 0 });
			}

			if (isCollapse) {
				gsap.set([...productsRef.current, titleRef.current, textRef.current], { opacity: 1 });
			}
		}
	}, [activeSection, isCollapse, isGatesActive]);

	return (
		<CollectionStyle
			ref={sectionRef}
			className={`collection-container primary-color9 ${isOpened ? 'active' : ''}`}>
			<div
				className='collection-header'
				onClick={() => isCollapse && onCollapseClick && onCollapseClick(id)}>
				<Text
					ref={titleRef}
					tag={key === 0 ? 'h1' : 'h2'}
					text={name}
					className='collection-title h4 minion3-medium uppercase'
				/>

				{winWidth < 768 ? <div className={`collection-plus-cont`} /> : null}
			</div>

			<div
				ref={bodyRef}
				className='collection-body'>
				<Text
					ref={textRef}
					text={text}
					className='collection-text h6 minion3-regular'
				/>
				<div className={`explore-btn-wrap`}>
					<Button
						text={`exploreAll`}
						url={`/products/collection/${slug}`}
					/>
				</div>

				<div className='row collection-products-row'>
					{products.map((product, k) => {
						return (
							product.featured_image &&
							product.types && (
								<div
									ref={(ref) => (productsRef.current[k] = ref)}
									key={k}
									className='col-4 col-ts-6 col-m-12 collection-product-cont'>
									<ProductCard
										image={product.featured_image}
										name={product.name}
										description={product.types}
										url={`${router.pathname}/${product.slug}`}
									/>
								</div>
							)
						);
					})}
				</div>
			</div>
		</CollectionStyle>
	);
};

export default withUIContext(Collection, ['winWidth', 'isGatesActive']);
